/**
 * ProcessSteps — numbered "How it works" section primitive for
 * marketing surfaces.
 *
 * What this is for:
 *   The 3-step "SLP → kid → parent" flow lives on the demo landing.
 *   Future marketing surfaces (a "For districts" page, a one-pager
 *   sidebar) can reuse the same primitive without redoing the
 *   numbered-circle visual.
 *
 * Visual treatment:
 *   - Each step renders as a vertical card on mobile, horizontal
 *     card on ≥768px.
 *   - The step number is a mint-green circle with a white numeral
 *     (see `.process-step-number` in `web/src/index.css`).
 *   - The illustration slot is a React node so the parent supplies
 *     the inline SVG; this component owns only the chrome.
 */
import type { ReactElement, ReactNode } from 'react';

export interface ProcessStep {
  /** Step number, 1-indexed. */
  number: number;
  /** Short title — sentence-case (e.g. "SLP sends a link"). */
  title: string;
  /** Body copy — one short paragraph. */
  body: string;
  /** Visual identifier (40–60px). */
  illustration: ReactNode;
}

export interface ProcessStepsProps {
  /** Section heading — defaults to "How it works". */
  heading?: string;
  /** Section subheading — one short line. */
  subheading?: string;
  steps: ReadonlyArray<ProcessStep>;
}

export function ProcessSteps({
  heading = 'How it works',
  subheading,
  steps,
}: ProcessStepsProps): ReactElement {
  return (
    <section
      className="process-steps"
      aria-label={heading}
      data-testid="process-steps"
    >
      <header className="process-steps-header">
        <h2>{heading}</h2>
        {subheading ? <p className="tagline">{subheading}</p> : null}
      </header>
      <ol className="process-steps-list">
        {steps.map((step) => (
          <li
            key={step.number}
            className="process-step"
            data-testid={`process-step-${step.number}`}
          >
            <div className="process-step-number" aria-hidden="true">
              {step.number}
            </div>
            <div className="process-step-illustration" aria-hidden="true">
              {step.illustration}
            </div>
            <h3 className="process-step-title">{step.title}</h3>
            <p className="process-step-body">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default ProcessSteps;